import { QueryClientProvider } from "@tanstack/react-query";
import { Switch, Route, Router as WouterRouter } from "wouter";
import { queryClient } from "./lib/queryClient";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import NotFound from "@/pages/not-found";
import HomePage from "@/pages/home-page";
import EquipmentCapturePage from "@/pages/equipment-capture-page";
import AuthPage from "@/pages/auth-page";
import AnalysisPage from "@/pages/analysis-page";
import ResultsPage from "@/pages/results-page";
import SavedAnalysesPage from "@/pages/saved-analyses-page";
import ProfilePage from "@/pages/profile-page";
import AuditLogsPage from "@/pages/audit-logs-page";
import NyserdaPage from "@/pages/nyserda-page";
import OsemProposalsPage from "@/pages/osem-proposals-page";
import OsemOnboardingPage from "@/pages/osem-onboarding-page";
import OsemGlidepathPage from "@/pages/osem-glidepath-page";
import OsemLightingPage from "@/pages/osem-lighting-page";
import EnergyManagementPlanPage from "@/pages/energy-management-plan-page";
import CustomerAccessPage from "@/pages/customer-access-page";
import ResetPasswordPage from "@/pages/reset-password-page";
import AdminSettingsPage from "@/pages/admin-settings-page";
import { ProtectedRoute } from "@/lib/protected-route";
import { movedToEofdd, EOFDD_BASE } from "@/components/moved-to-eofdd";

// NYSERDA OsEM moved to Energy Optimizer (2026-10-18): these pages show a notice first (the old page stays reachable).
const NyserdaMoved = movedToEofdd(NyserdaPage, "NYSERDA tracking", () => `${EOFDD_BASE}/osem-program`);
const ProposalsMoved = movedToEofdd(HomePage, "NYSERDA OsEM", () => `${EOFDD_BASE}/osem-onboarding`);
const EmpMoved = movedToEofdd(EnergyManagementPlanPage, "The Energy Management Plan", (p) => `${EOFDD_BASE}/osem-program?facilityId=pump-${encodeURIComponent(p.id || "")}`);
const GlidepathMoved = movedToEofdd(OsemGlidepathPage, "The Task 10 glidepath", (p) => `${EOFDD_BASE}/osem-program?facilityId=pump-${encodeURIComponent(p.id || "")}`);
const LightingMoved = movedToEofdd(OsemLightingPage, "The lighting auditor", (p) => `${EOFDD_BASE}/osem-program?facilityId=pump-${encodeURIComponent(p.id || "")}`);
const OnboardingMoved = movedToEofdd(OsemOnboardingPage, "This OsEM proposal", (p) => `${EOFDD_BASE}/osem-onboarding?facilityId=pump-${encodeURIComponent(p.id || "")}`);
import { AuthProvider } from "@/hooks/use-auth";
import { CustomerProvider } from "@/hooks/use-customer";
import { I18nextProvider } from "react-i18next";
import i18n from "@/lib/i18n";

function AuthRouter() {
  return (
    <Switch>
      <Route path="/auth" component={AuthPage} />
      <ProtectedRoute path="/reset-password" component={ResetPasswordPage} />
      <ProtectedRoute path="/" component={HomePage} />
      <ProtectedRoute path="/facilities" component={HomePage} />
      <ProtectedRoute path="/equipment-capture" component={EquipmentCapturePage} />
      <ProtectedRoute path="/analysis/new" component={AnalysisPage} />
      <ProtectedRoute path="/analysis/:id" component={ResultsPage} />
      <ProtectedRoute path="/analyses" component={SavedAnalysesPage} />
      <ProtectedRoute path="/nyserda" component={NyserdaMoved} />
      <ProtectedRoute path="/osem-proposals" component={ProposalsMoved} />
      <ProtectedRoute path="/osem-onboarding" component={ProposalsMoved} />
      <ProtectedRoute path="/osem-onboarding/:id/emp" component={EmpMoved} />
      <ProtectedRoute path="/osem-onboarding/:id/glidepath" component={GlidepathMoved} />
      <ProtectedRoute path="/osem-onboarding/:id/lighting" component={LightingMoved} />
      <ProtectedRoute path="/osem-onboarding/:id" component={OnboardingMoved} />
      <ProtectedRoute path="/profile" component={ProfilePage} />
      <ProtectedRoute path="/settings/customer-access" component={CustomerAccessPage} roles={["Admin"]} />
      <ProtectedRoute path="/settings/system" component={AdminSettingsPage} roles={["Admin"]} />
      <ProtectedRoute path="/audit-logs" component={AuditLogsPage} roles={["Admin"]} />
      <Route component={NotFound} />
    </Switch>
  );
}

function Router() {
  // Determine router base dynamically: when accessed via app.onitnetworking.com/ooe/ the path
  // starts with /ooe, so use that as the base. When accessed directly on pump.onitnetworking.com
  // the path has no /ooe prefix, so use "" (root). This allows both entry points to work.
  const viteBase = import.meta.env.BASE_URL.replace(/\/$/, "");  // "/ooe" in production
  const pathStartsWithBase = viteBase && window.location.pathname.startsWith(viteBase + "/");
  const isExactBase = viteBase && window.location.pathname === viteBase;
  const base = (pathStartsWithBase || isExactBase) ? viteBase : "";
  return (
    <WouterRouter base={base}>
      <Switch>
        <Route path="/auth" component={AuthPage} />
        <Route>
          <AuthProvider>
            <CustomerProvider>
              <AuthRouter />
            </CustomerProvider>
          </AuthProvider>
        </Route>
      </Switch>
    </WouterRouter>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <I18nextProvider i18n={i18n}>
        <TooltipProvider>
          <Toaster />
          <Router />
        </TooltipProvider>
      </I18nextProvider>
    </QueryClientProvider>
  );
}

export default App;
